const MOBILE_MODAL_TRIGGER_SELECTOR = [
  '[aria-modal="true"]',
  '.floating-messenger.is-open:not(.is-mobile-page)',
  '.modal-overlay',
  '.terms-modal-overlay',
  '.job-preview-overlay',
  '.application-workspace-overlay',
  '.guided-portal-tour',
  '.resume-playground-editor-mobile-overlay',
  '.jt-ai-editor-overlay',
  '[class*="-backdrop"]'
].join(',');

const MOBILE_MODAL_SURFACE_SELECTOR = [
  '[aria-modal="true"]',
  '.floating-messenger-panel',
  '.login-modal',
  '.register-modal',
  '.job-interest-modal',
  '.job-preview-modal',
  '.application-workspace-modal',
  '.message-company-profile-modal',
  '.terms-modal'
].join(',');

// Mobile Messages is intentionally rendered as a full-screen page instead of
// a dialog. It therefore must not use the dialog's `position: fixed` body
// lock (that would interfere with the keyboard viewport), but the document
// behind it still has to be locked against touch-scroll chaining.
const MOBILE_MESSAGE_PAGE_SELECTOR = [
  '.floating-messenger.is-open.is-mobile-page',
  '#root .messenger-inbox.is-mobile-message-page'
].join(',');

const MOBILE_MESSAGE_PAGE_SURFACE_SELECTOR = [
  '#jumptake-floating-messenger.is-open.is-mobile-page',
  '#root .messenger-inbox.is-mobile-message-page'
].join(',');

const MOBILE_MESSAGE_SCROLL_SELECTOR = [
  '.floating-messenger-contacts',
  '.floating-messenger-messages',
  '.floating-messenger-assistant-chat .public-ai-chat-messages',
  '.messenger-inbox .message-thread',
  '.messenger-inbox .public-ai-chat-messages'
].join(',');

const isVisibleModal = (element) => {
  if (!element || element.hidden) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const hasVisibleMobileModal = () => Array.from(
  document.querySelectorAll(MOBILE_MODAL_TRIGGER_SELECTOR)
).some(isVisibleModal);

const hasVisibleMobileMessagePage = () => Array.from(
  document.querySelectorAll(MOBILE_MESSAGE_PAGE_SELECTOR)
).some(isVisibleModal);

export const installMobileModalScrollLock = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};

  const mobileQuery = window.matchMedia('(max-width: 768px) and (hover: none)');
  let locked = false;
  let messagePageLocked = false;
  let scrollX = 0;
  let scrollY = 0;
  let syncQueued = false;
  let syncFrame = 0;
  let lastTouchY = null;

  const lock = () => {
    if (locked) return;
    scrollX = window.scrollX;
    scrollY = window.scrollY;
    document.documentElement.style.setProperty('--jt-mobile-modal-scroll-x', `${-scrollX}px`);
    document.documentElement.style.setProperty('--jt-mobile-modal-scroll-y', `${-scrollY}px`);
    document.documentElement.classList.add('jt-mobile-modal-open');
    document.body.classList.add('jt-mobile-modal-open');
    locked = true;
  };

  const unlock = () => {
    if (!locked) return;
    document.documentElement.classList.remove('jt-mobile-modal-open');
    document.body.classList.remove('jt-mobile-modal-open');
    document.documentElement.style.removeProperty('--jt-mobile-modal-scroll-x');
    document.documentElement.style.removeProperty('--jt-mobile-modal-scroll-y');
    locked = false;
    window.scrollTo(scrollX, scrollY);
  };

  const lockMessagePage = () => {
    if (messagePageLocked) return;
    document.documentElement.classList.add('jt-mobile-message-page-open');
    document.body.classList.add('jt-mobile-message-page-open');
    messagePageLocked = true;
  };

  const unlockMessagePage = () => {
    if (!messagePageLocked) return;
    document.documentElement.classList.remove('jt-mobile-message-page-open');
    document.body.classList.remove('jt-mobile-message-page-open');
    messagePageLocked = false;
  };

  const sync = () => {
    syncQueued = false;
    syncFrame = 0;
    if (mobileQuery.matches && hasVisibleMobileModal()) lock();
    else unlock();

    if (mobileQuery.matches && hasVisibleMobileMessagePage()) lockMessagePage();
    else unlockMessagePage();
  };

  const queueSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    syncFrame = window.requestAnimationFrame(sync);
  };

  const getMessageScrollArea = (target) => {
    if (!target?.closest?.(MOBILE_MESSAGE_PAGE_SURFACE_SELECTOR)) return null;

    let element = target instanceof Element ? target : target?.parentElement;
    while (element && element !== document.body) {
      if (
        element.matches?.(MOBILE_MESSAGE_SCROLL_SELECTOR)
        && element.scrollHeight > element.clientHeight
      ) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  };

  const handleTouchStart = (event) => {
    lastTouchY = event.touches?.[0]?.clientY ?? null;
  };

  const preventBackdropScroll = (event) => {
    if (locked && !event.target?.closest?.(MOBILE_MODAL_SURFACE_SELECTOR)) {
      event.preventDefault();
      return;
    }

    if (messagePageLocked && !event.target?.closest?.(MOBILE_MESSAGE_PAGE_SURFACE_SELECTOR)) {
      event.preventDefault();
      return;
    }

    if (!messagePageLocked) return;

    const touchY = event.touches?.[0]?.clientY;
    if (typeof touchY !== 'number' || lastTouchY === null) return;

    const touchDeltaY = touchY - lastTouchY;
    lastTouchY = touchY;
    const scrollArea = getMessageScrollArea(event.target);

    // iOS Safari ignores overscroll-behavior on nested scroll areas. Stop the
    // gesture only at an edge so normal conversation scrolling stays native,
    // but the gesture can never escape into the page behind Messages.
    if (!scrollArea) {
      event.preventDefault();
      return;
    }

    const atTop = scrollArea.scrollTop <= 0;
    const atBottom = scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 1;
    if ((touchDeltaY > 0 && atTop) || (touchDeltaY < 0 && atBottom)) {
      event.preventDefault();
    }
  };

  const observer = new MutationObserver(queueSync);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'aria-modal', 'class', 'hidden', 'open', 'style']
  });
  mobileQuery.addEventListener?.('change', queueSync);
  document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
  document.addEventListener('touchmove', preventBackdropScroll, { passive: false, capture: true });
  sync();

  return () => {
    observer.disconnect();
    if (syncFrame) window.cancelAnimationFrame(syncFrame);
    mobileQuery.removeEventListener?.('change', queueSync);
    document.removeEventListener('touchstart', handleTouchStart, true);
    document.removeEventListener('touchmove', preventBackdropScroll, true);
    unlock();
    unlockMessagePage();
  };
};
